import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import HBText, { TTextType } from '../HBText/HBText';
import { newTextStyles } from '../HBText/HBText.styles';
import HBBox from '../HBBox/HBBox';
import { withStyles } from '../../styles/layout/withStyles';
import { spacing } from '../../styles';
import type { TExtendedSize, TLayoutAlignment } from '../../tokens/types';
import type { HBFieldControlPlacement } from './HBFieldControl.types';

/** Label/subLabel typography per size — mirrors the HB*Field label scale. */
export const LABEL_TEXT_TYPE: Record<TExtendedSize, TTextType> = {
    xs: 'body12',
    sm: 'body14',
    md: 'body14',
    lg: 'body16',
    xl: 'body18'
};

/** Caption typography per size — one step below the label scale. */
export const CAPTION_TEXT_TYPE: Record<TExtendedSize, TTextType> = {
    xs: 'body10',
    sm: 'body12',
    md: 'body12',
    lg: 'body14',
    xl: 'body16'
};

const LABEL_PLACEMENT_PADDING: Record<
    HBFieldControlPlacement,
    FlattenSimpleInterpolation
> = {
    top: css`
        padding-bottom: ${spacing['4']};
    `,
    right: css`
        padding-left: ${spacing['8']};
        /* Takes the width the control no longer grows into (see ControlWrapper),
           so a long label wraps within the field instead of overflowing it. */
        flex: 1;
        min-width: 0;
    `,
    left: css`
        padding-right: ${spacing['8']};
    `
};

export const FieldContainer = styled.div<{
    $width: string;
    $margin?: string;
    $disabled?: boolean;
}>`
    display: flex;
    flex-direction: column;
    width: ${({ $width }) => $width};
    ${({ $margin }) =>
        $margin &&
        css`
            margin: ${$margin};
        `}
    ${({ $disabled }) =>
        $disabled &&
        css`
            &,
            * {
                cursor: not-allowed !important;
            }
        `}
`;

// Plain layout wrapper for the label column (label row, subLabel, caption).
// Not a <label> itself — the tooltip trigger sits in the label row as a sibling
// of StyledLabel, never a descendant of it, so a screen reader interacting with
// the <label> can't route focus/activation into the nested tooltip button.
export const LabelStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing['2']};

    i {
        display: flex;
    }
`;

// The actual <label htmlFor>, wrapping only the label text (+ required asterisk).
export const StyledLabel = styled.label<{
    $clickable: boolean;
}>`
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

/** Typography of the hint/error/disabled message. Shared with the component so
 *  the reserved height below cannot drift from what the text actually renders. */
export const MESSAGE_TEXT_TYPE = 'body12';

/** One line of the message, read from the type scale rather than copied. */
const MESSAGE_LINE_HEIGHT = newTextStyles[MESSAGE_TEXT_TYPE].lineHeight;

/**
 * Height the message area occupies under `reserveMessageSpace`: one line plus its
 * gap to the control. The label column reserves the same amount at its foot, which
 * splits it into a cell matching the control and a cell matching the message area.
 */
const MESSAGE_AREA_RESERVED = `calc(${MESSAGE_LINE_HEIGHT} + ${spacing['4']})`;

const LABEL_JUSTIFY: Record<TLayoutAlignment, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end'
};

// Wraps the label + counter row and carries the placement gap to the control
// (or, for top placement, to the control below). This has to live on the
// wrapper rather than on LabelStack itself: for left/right placement the
// counter renders as LabelStack's sibling *inside* this wrapper, so padding
// on LabelStack alone would land between the label and the counter instead
// of between the counter and the control.
export const LabelCounterWrapper = styled(withStyles(HBBox))<{
    $placement: HBFieldControlPlacement;
    $hidden?: boolean;
    $labelAlignment?: TLayoutAlignment;
    $reserveMessageSpace?: boolean;
    $hasMessage?: boolean;
    $isMessageVisible?: boolean;
}>`
    ${({ $placement }) => LABEL_PLACEMENT_PADDING[$placement]}
    /*
     * Beside the control the label column is split in two: it stretches to the
     * row's height and reserves the message area's height at its foot, leaving a
     * content box the same height as the control. justify-content then places
     * the label inside that box, so 'center' lands on the control's centre
     * rather than the centre of control + message. Only meaningful here — in
     * 'top' placement the label is stacked above and there is no cross axis.
     *
     * The foot cell mirrors whatever the message area actually occupies, which is
     * not the same as whether the reserve is on. Reserved, the area always has
     * height, so the foot is always padded. Unreserved, the area has height only
     * while a message is shown — so the foot has to follow it, appearing under the
     * same conditions (focus-within, or an already-visible message). Padding it
     * when the area is empty would push the label up; not padding it when the area
     * is filled would let the label drift down by half the message's height, which
     * is a visible jump on a field the consumer never opted into.
     *
     * The focus rule uses && rather than &: a non-leading & compiles to the shared
     * componentId, so one side-placed field with a hint would pad the label of
     * every focused field on the page, top placement included. && keeps it on
     * this instance's class.
     */
    ${({
        $placement,
        $labelAlignment,
        $reserveMessageSpace,
        $hasMessage,
        $isMessageVisible
    }) =>
        $placement !== 'top' &&
        $labelAlignment &&
        css`
            align-self: stretch;
            display: flex;
            flex-direction: column;
            justify-content: ${LABEL_JUSTIFY[$labelAlignment]};
            ${$reserveMessageSpace
                ? css`
                      padding-bottom: ${MESSAGE_AREA_RESERVED};
                  `
                : $hasMessage &&
                  css`
                      ${FieldContainer}:focus-within && {
                          padding-bottom: ${MESSAGE_AREA_RESERVED};
                      }

                      ${$isMessageVisible &&
                      css`
                          padding-bottom: ${MESSAGE_AREA_RESERVED};
                      `}
                  `}
        `}
    ${({ $hidden }) =>
        $hidden &&
        css`
            visibility: hidden;
        `}
`;

// Always rendered, so the live region inside it is never inserted alongside its
// own text (HBUI-650). Holds the gap to the control, which keeps the spacing
// identical whether the message inside is the live region's (error/disabled) or
// the hint rendered beside it.
//
// Under `reserveMessageSpace` it is also always at least one line tall, so a
// field does not change height the moment a message appears and a longer message
// grows it. Otherwise it collapses to nothing until a message is actually shown,
// matching HBBaseFormField: a field with no message is exactly the height of its
// control. The collapse has to gate `margin-top` too — a 0-height box with a live
// margin would still add 4px to every field.
export const MessageArea = styled.div<{
    $reserve: boolean;
    $hasMessage: boolean;
    $isVisible: boolean;
}>`
    margin-top: ${spacing['4']};
    min-height: ${MESSAGE_LINE_HEIGHT};

    ${({ $reserve, $hasMessage, $isVisible }) =>
        !$reserve &&
        css`
            display: none;
            margin-top: 0;
            min-height: 0;

            /* Gated on there being a message at all: revealing on focus alone
               would add the gap to every field the moment it is focused, even
               one that has nothing to say. && keeps that gate per instance
               (see LabelCounterWrapper). */
            ${$hasMessage &&
            css`
                ${FieldContainer}:focus-within && {
                    display: block;
                    margin-top: ${spacing['4']};
                }

                ${$isVisible &&
                css`
                    display: block;
                    margin-top: ${spacing['4']};
                `}
            `}
        `}
`;

export const MessageText = styled(HBText)<{ $isVisible: boolean }>`
    display: none;

    ${FieldContainer}:focus-within & {
        display: block;
    }

    ${({ $isVisible }) =>
        $isVisible &&
        css`
            display: block;
        `}
`;

// Always-mounted live region. Kept permanently in the DOM rather than inserted
// along with the message: `aria-describedby` is only read when the control
// receives focus, so a message that changes while focus stays put is silent
// (HBUI-650), and a live region inserted at the same moment its text appears is
// announced inconsistently across screen readers. Unstyled — MessageArea owns
// the spacing and the reserved height for both this and the hint beside it.
export const MessageLiveRegion = styled.div``;
